import * as React from 'react';
import clsx from 'clsx';

interface AuthTitleBlockProps {
  /** Whether the current view is the register form. */
  isRegister: boolean;
  /** Callback to switch between login/register views. */
  onSwitch?: () => void;
}

/**
 * Renders the auth page title and subtitle.
 *
 * Mobile register: shows "Create Account" + "Already have an account? Sign in" link + divider.
 * Desktop register: shows "Create an account" + subtitle.
 * Login (both): shows "Sign in" + subtitle.
 */
export const AuthTitleBlock: React.FC<AuthTitleBlockProps> = ({ isRegister, onSwitch }) => {
  const titleDesktop = isRegister ? 'Create an account' : 'Sign in';
  const titleMobile = isRegister ? 'Create Account' : 'Sign in';
  const subtitleText = isRegister
    ? 'Start your journey, use free credits to chat with any ECHO.'
    : 'At d.echoes we enable you to turn your digital traces, alongside your experience, your voice, and your personality into a living digital version of yourself. One that can interact, with everyone 24/7.';

  return (
    <div className='flex flex-col'>
      <h1
        className={clsx(
          'font-proxima font-bold leading-25 tracking-normal',
          'text-white fold:text-brand-800',
          'text-[length:var(--vs-30,30px)]',
          isRegister ? 'text-center fold:text-left fold:text-32' : 'fold:text-38',
          'fold:text-left'
        )}
      >
        <span className='fold:hidden'>{titleMobile}</span>
        <span className='hidden fold:inline'>{titleDesktop}</span>
      </h1>

      {isRegister && onSwitch && (
        <div className='mt-[var(--vs-11,11px)] flex flex-col items-center fold:hidden'>
          <p className='font-sans text-14 font-normal leading-21 tracking-normal text-white'>
            Already have an account?{' '}
            <button type='button' onClick={onSwitch} className='font-bold underline'>
              Sign in
            </button>
          </p>
          <div className='mb-[var(--vs-24,24px)] mt-[var(--vs-20,20px)] flex w-full items-center gap-3'>
            <div className='h-px flex-1 border-t border-[#706E6E] bg-[#D9FBBE]' />
            <span className='shrink-0 font-sans text-14 font-normal text-white'>
              create account with
            </span>
            <div className='h-px flex-1 border-t border-[#706E6E] bg-[#D9FBBE]' />
          </div>
        </div>
      )}

      <p
        className={clsx(
          'font-sans font-normal leading-21 tracking-normal',
          isRegister ? 'text-14' : 'text-13',
          'text-white fold:text-brand-750',
          'mb-[var(--vs-20,20px)] mt-[var(--vs-11,11px)]',
          !isRegister && 'fold:mb-[40px] fold:mt-[23px]',
          isRegister && 'hidden fold:mb-4 fold:mt-2 fold:block'
        )}
      >
        {subtitleText}
      </p>
    </div>
  );
};
